/**
 * OS TIPOS DE EQUIPAMENTO QUE O LABORATÓRIO ATENDE.
 *
 * A lista mora aqui, e não na tela, por dois motivos:
 *
 *  1. Ela é REGRA, não desenho: o servidor usa a mesma lista para reconhecer o
 *     tipo que vem escrito na solicitação do Relatórios (texto livre) e gravar
 *     um valor da lista — sem isso o cadastro automático nasceria sempre sem
 *     tipo, e o campo do formulário viraria trabalho manual repetido.
 *  2. Lista copiada em dois arquivos desanda: alguém acrescenta "Bucha" na tela
 *     e o servidor continua sem reconhecer. `src/config.ts` importa DESTE
 *     arquivo — para incluir ou renomear um tipo, mexa só aqui.
 *
 * Ordem: a que o laboratório passou (do mais comum para o menos), não
 * alfabética — é a ordem em que a pessoa procura no menu.
 *
 * NÃO é uma lista fechada por validação: o campo grava o que a tela mandar. Se
 * chegar um equipamento fora da lista (base antiga, importação), ele continua
 * aparecendo no cadastro em vez de sumir.
 */
export const TIPOS_EQUIPAMENTO = [
  "Transformador",
  // TC e TP são equipamentos DIFERENTES do transformador de força, e é por isso
  // que estão aqui com nome próprio: sem eles na lista, "Transformador de
  // corrente" era reconhecido como "Transformador" (a palavra está contida no
  // texto) e passava a ser julgado pela tabela do transformador de força — a
  // rigidez de um TC não é a de um trafo de 100 MVA. É o que o item 10 da base
  // de limites proíbe em letras maiúsculas.
  //
  // Como a base da NBR 10576 não traz tabela para eles, TC e TP ficam SEM
  // diagnóstico automático quando o óleo está em serviço: a tabela 3 exige
  // "Transformador|Reator" e não casa. Isso é proposital — o parecer deles é
  // escrito à mão até o laboratório cadastrar a régua própria.
  "Transformador de corrente",
  "Transformador de potencial",
  "Comutador",
  "Eletroímã",
  "Tanque",
  "Reator",
  "Precipitador",
  "Regulador de tensão",
  "Tanque auxiliar",
  "Capacitor",
  "Chave seccionadora",
  "Disjuntor",
  "Retificador",
  "Reostato",
  "Religador",
  "Relé de gás",
] as const;

/** Texto comparável: sem acento, sem caixa e sem espaço sobrando. */
const chave = (v: unknown): string =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

/**
 * Casa um texto livre com um dos tipos da lista.
 *
 * O Relatórios manda o equipamento como texto ("transformador", "TRAFO 01 —
 * Transformador"...). Aqui só entra o que for reconhecível: devolver o texto
 * cru encheria o cadastro de variações da mesma coisa, que é justamente o que a
 * lista existe para evitar. Sem casamento, devolve null e o campo fica em
 * branco para alguém escolher.
 */
export function normalizarTipoEquipamento(v: unknown): string | null {
  const alvo = chave(v);
  if (!alvo) return null;
  const exato = TIPOS_EQUIPAMENTO.find((t) => chave(t) === alvo);
  if (exato) return exato;
  // "Transformador de força", "Relé de gás Buchholz": o tipo aparece dentro do
  // texto. Do mais específico para o menos, senão "Tanque" ganharia de "Tanque
  // auxiliar" em qualquer frase que contivesse os dois — e, pior, "Transformador"
  // ganharia de "Transformador de corrente", mandando um TC ser julgado pela
  // tabela do transformador de força.
  const porTamanho = [...TIPOS_EQUIPAMENTO].sort((a, b) => b.length - a.length);
  return porTamanho.find((t) => alvo.includes(chave(t))) ?? null;
}
